import { useEffect, useState } from 'react';
import { ArrowClockwise, Brain, CaretDown, CaretUp, RssSimple, Sparkle } from '@phosphor-icons/react';
import ContextHelpButton from '../ContextHelpButton';

function ClientBrainMemoryIndicator({ project, t }) {
  const [open, setOpen] = useState(false);

  if (!project?.client_brain_id) return null;

  const copy = (key, defaultValue) => t(`project.clientBrainMemory.${key}`, { defaultValue });

  return (
    <div className="relative" data-testid="client-brain-memory-indicator">
      <button
        type="button"
        data-testid="client-brain-memory-badge"
        className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-semibold text-indigo-100 transition-colors hover:bg-white/[0.06]"
        style={{
          background: 'rgba(99,102,241,0.12)',
          borderColor: 'rgba(165,180,252,0.36)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
        }}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="client-brain-memory-panel"
        title={copy('badgeTitle', 'Mostra cosa sta influenzando la generazione')}
      >
        <Brain size={13} weight="fill" />
        <span>{copy('badge', 'Client Brain · memoria attiva')}</span>
      </button>

      {open && (
        <div
          id="client-brain-memory-panel"
          data-testid="client-brain-memory-panel"
          className="absolute bottom-full right-0 z-50 mb-2 w-[min(330px,calc(100vw-24px))] rounded-xl border p-3.5 text-left shadow-2xl"
          style={{
            background: 'rgba(10,14,28,0.98)',
            borderColor: 'rgba(165,180,252,0.32)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-400/10 text-indigo-200">
              <Brain size={15} weight="fill" />
            </div>
            <div>
              <p className="text-xs font-semibold text-white">
                {copy('title', 'Cosa sta influenzando la generazione')}
              </p>
              <p className="text-[10px] text-[var(--text-muted)]">
                {copy('subtitle', 'Memoria condivisa tra le campagne dello stesso cliente')}
              </p>
            </div>
          </div>

          <div className="space-y-2.5 text-[11px] leading-relaxed text-[var(--text-secondary)]">
            <div>
              <p className="font-semibold text-indigo-100">{copy('identityTitle', 'Identità cliente')}</p>
              <p>{copy('identityBody', 'Posizionamento, target, offerte, Tone of Voice, regole e documenti condivisi.')}</p>
            </div>
            <div>
              <p className="font-semibold text-indigo-100">{copy('historyTitle', 'Storico editoriale')}</p>
              <p>{copy('historyBody', 'Fino a 50 contenuti recenti dello stesso cliente aiutano a evitare ripetizioni e a mantenere continuità tra campagne.')}</p>
            </div>
            <div>
              <p className="font-semibold text-indigo-100">{copy('analyticsTitle', 'Performance')}</p>
              <p>{copy('analyticsBody', 'Le metriche Analytics già sincronizzate vengono considerate quando disponibili, senza comandare da sole la strategia.')}</p>
            </div>
          </div>

          <p className="mt-3 border-t border-white/10 pt-2.5 text-[10px] font-medium text-indigo-100">
            {copy('priority', 'Il brief e le istruzioni della campagna corrente restano prioritari.')}
          </p>
        </div>
      )}
    </div>
  );
}

export default function FeedStrips({
  isMobile,
  project,
  feedItems,
  feedItemsLoading,
  feedItemsError,
  aiFeedItems,
  pinnedItemIds,
  refreshingFeeds,
  refreshingAi,
  refreshFeeds,
  refreshAiSuggestions,
  setSelectedFeedItem,
  canRssFeeds,
  t,
}) {
  const storageKey = `sketchario.feedStripsCollapsed.${project?.id || 'global'}`;
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(storageKey) !== 'false');
    } catch {
      setCollapsed(true);
    }
  }, [storageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? 'true' : 'false');
    } catch {
      // Local storage can be unavailable in private contexts; the UI still works.
    }
  }, [collapsed, storageKey]);

  const hasClientBrain = Boolean(project?.client_brain_id);
  if (isMobile) {
    if (!hasClientBrain) return null;
    return (
      <div
        data-testid="client-brain-memory-mobile-strip"
        className="flex flex-shrink-0 justify-end border-t px-4 py-2"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
      >
        <ClientBrainMemoryIndicator project={project} t={t} />
      </div>
    );
  }

  const lockedFeedPlaceholders = [1, 2, 3];
  const feedCount = canRssFeeds ? feedItems.length : 0;
  const aiCount = canRssFeeds ? aiFeedItems.length : 0;

  return (
    <div
      data-testid="feed-strips"
      className="relative flex-shrink-0 border-t"
      style={{
        background: collapsed
          ? 'linear-gradient(90deg, rgba(20,32,62,0.98) 0%, rgba(34,25,70,0.98) 54%, rgba(14,48,65,0.98) 100%)'
          : 'var(--bg-secondary)',
        borderColor: collapsed ? 'rgba(129,140,248,0.55)' : 'var(--border-color)',
        boxShadow: collapsed ? '0 -8px 28px rgba(56,72,160,0.18)' : 'none',
      }}
    >
      {hasClientBrain && (
        <div className="flex justify-end px-4 pt-2 md:px-6" style={{ paddingRight: '3rem' }}>
          <ClientBrainMemoryIndicator project={project} t={t} />
        </div>
      )}
      <div className="absolute right-2 top-2 z-20">
        <ContextHelpButton articleId="feeds" label="Guida Feed" />
      </div>
      {collapsed && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full border border-indigo-300/40 bg-[#17213d] px-3 py-0.5 shadow-lg">
          <span className="block h-1 w-10 rounded-full bg-gradient-to-r from-indigo-300 via-fuchsia-300 to-cyan-300 opacity-90" />
        </div>
      )}
      <button
        type="button"
        className="w-full px-4 pr-12 md:px-6 md:pr-12 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-white/[0.05] transition-colors"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
      >
        <span className={`min-w-0 flex items-center gap-2 text-[10px] font-semibold uppercase ${collapsed ? 'text-indigo-100' : 'text-[var(--text-muted)]'}`}>
          <RssSimple size={13} weight={collapsed ? 'fill' : 'regular'} />
          <span className="truncate">{t('project.feeds.newsReddit')} ({t('project.feeds.beta')}) / {t('project.feeds.aiSuggestions')} — {project.sector}</span>
        </span>
        <span className={`flex items-center gap-3 text-[10px] ${collapsed ? 'text-indigo-100' : 'text-[var(--text-muted)]'}`}>
          <span>{feedCount} RSS · {aiCount} AI</span>
          <span className="hidden lg:inline font-semibold">{collapsed ? t('project.feeds.expandStrip') : t('project.feeds.collapseStrip')}</span>
          {collapsed ? <CaretUp size={15} weight="bold" /> : <CaretDown size={15} weight="bold" />}
        </span>
      </button>

      {!collapsed && (
        <>
          {!canRssFeeds && (
            <div className="absolute inset-x-0 top-9 bottom-0 z-10 flex items-center justify-center p-4">
              <div className="rounded-full border border-[var(--gradient-start)]/40 bg-[rgba(8,10,18,0.88)] px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm">
                {t('project.feeds.proOnly')}
              </div>
            </div>
          )}
          <div className={`px-4 md:px-6 pt-1 pb-2 ${!canRssFeeds ? 'opacity-40 pointer-events-none select-none' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase flex items-center gap-1 truncate"><RssSimple size={12} /> {t('project.feeds.newsReddit')} ({t('project.feeds.beta')}) — {project.sector}</p>
              <button className="text-[10px] text-[var(--text-muted)] hover:text-white flex items-center gap-1 flex-shrink-0" disabled={!canRssFeeds || refreshingFeeds} onClick={refreshFeeds}>
                <ArrowClockwise size={10} className={refreshingFeeds ? 'animate-spin' : ''} /> {refreshingFeeds ? '...' : t('project.feeds.refresh')}
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}>
              {(canRssFeeds ? feedItems.slice(0, 6) : lockedFeedPlaceholders).map(item => (
                <div
                  key={item.id || `rss-strip-placeholder-${item}`}
                  className="flex-shrink-0 w-44 md:w-52 p-2.5 md:p-3 rounded-lg cursor-pointer hover:border-[var(--gradient-start)] transition-colors relative"
                  style={{ background: 'var(--bg-card)', border: `1px solid ${canRssFeeds && pinnedItemIds.has(item.id) ? 'var(--gradient-start)' : 'var(--border-color)'}` }}
                  onClick={() => canRssFeeds && setSelectedFeedItem({ ...item, _type: 'rss' })}
                >
                  {canRssFeeds && pinnedItemIds.has(item.id) && <span className="absolute top-1 right-1 text-[9px]">📌</span>}
                  <p className="text-xs font-medium line-clamp-2 mb-1 pr-3">{canRssFeeds ? item.title : t('project.feeds.lockedTitle')}</p>
                  <p className="text-[10px] text-[var(--text-muted)] line-clamp-1">{canRssFeeds ? item.feed_name : t('project.feeds.lockedSubtitle')}</p>
                </div>
              ))}
              {/* FEED-UI-001: loading / error / empty are three distinct,
                  mutually exclusive states — an empty [] response is a
                  successful result, never confused with "still loading". */}
              {canRssFeeds && feedItemsLoading && (
                <p className="text-[10px] text-[var(--text-muted)]">{t('project.feeds.loadingItems')}</p>
              )}
              {canRssFeeds && !feedItemsLoading && feedItemsError && (
                <p className="text-[10px] text-[var(--text-muted)]">{t('project.feeds.itemsError')}</p>
              )}
              {canRssFeeds && !feedItemsLoading && !feedItemsError && feedItems.length === 0 && (
                <div className="text-[10px] text-[var(--text-muted)]">
                  <p>{t('project.feeds.itemsEmptyTitle')}</p>
                  <p>{t('project.feeds.itemsEmptyHint')}</p>
                </div>
              )}
            </div>
          </div>

          <div className={`px-4 md:px-6 pt-1 pb-3 border-t border-[var(--border-color)] ${!canRssFeeds ? 'opacity-40 pointer-events-none select-none' : ''}`} style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase flex items-center gap-1 truncate"><Sparkle size={12} weight="fill" /> {t('project.feeds.aiSuggestions')} — {project.sector}</p>
              <button className="text-[10px] text-[var(--text-muted)] hover:text-white flex items-center gap-1 flex-shrink-0" disabled={!canRssFeeds || refreshingAi} onClick={refreshAiSuggestions}>
                <ArrowClockwise size={10} className={refreshingAi ? 'animate-spin' : ''} /> {refreshingAi ? '...' : t('editor.regenerate')}
              </button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}>
              {(canRssFeeds ? aiFeedItems : lockedFeedPlaceholders).map(item => (
                <div
                  key={item.id || `ai-strip-placeholder-${item}`}
                  className="flex-shrink-0 w-44 md:w-52 p-2.5 md:p-3 rounded-lg cursor-pointer hover:border-[var(--accent-purple)] transition-colors relative"
                  style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.08) 100%)', border: `1px solid ${canRssFeeds && pinnedItemIds.has(item.id) ? 'rgba(168,85,247,0.6)' : 'rgba(99,102,241,0.15)'}` }}
                  onClick={() => canRssFeeds && setSelectedFeedItem({ ...item, _type: 'ai' })}
                >
                  {canRssFeeds && pinnedItemIds.has(item.id) && <span className="absolute top-1 right-1 text-[9px]">📌</span>}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${canRssFeeds && item.format === 'reel' ? 'bg-[var(--accent-pink)]' : 'bg-[var(--gradient-start)]'}`} />
                    <span className="text-[9px] font-semibold text-[var(--text-muted)] uppercase">{canRssFeeds && item.format ? t(`format.${item.format}`) : t('format.reel')}</span>
                    {canRssFeeds && item.trend_tag && <span className="text-[9px] text-[var(--accent-purple)]">#{item.trend_tag}</span>}
                  </div>
                  <p className="text-xs font-medium line-clamp-2 mb-1 pr-3">{canRssFeeds ? item.title : t('project.feeds.lockedAiTitle')}</p>
                  <p className="text-[10px] text-[var(--text-muted)] line-clamp-1">{canRssFeeds ? item.summary : t('project.feeds.lockedAiSubtitle')}</p>
                </div>
              ))}
              {canRssFeeds && aiFeedItems.length === 0 && <p className="text-[10px] text-[var(--text-muted)]">{t('project.feeds.aiEmpty')}</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
